import { useEffect, useMemo, useState } from "react";
import { api } from "../../services/apiClient";
import { officersEndpoint } from "./officerDirectory";
import { LocalWaitingPreview } from "./LocalWaitingPreview";
import { type MediaDevicePreferences } from "../../services/mediaDevicePreferences";
import { useI18n } from "../../i18n/localization";

interface Officer {
  officerId: number;
  userId: number;
  displayName: string;
  rank?: string;
  unit?: string;
  available: boolean;
}

// Pre-call — officer selection (docs/design/05-uiux-spec.md §3.2). Lists directory
// officers with availability; "התקשר" only for available officers.
export function PreCall({
  onCall,
  canCall = true,
  actionError,
  reloadKey = 0,
  prefs,
  onMicToggle
}: {
  onCall: (officerUserId: number, name: string) => void;
  canCall?: boolean;
  actionError?: string | null;
  // Changes whenever a presenceChanged event arrives, so the directory refetches and stops
  // showing officers that became unavailable (and shows ones that came back).
  reloadKey?: number;
  // Post-readiness media setup — drives the local preview and mic control on this screen.
  prefs: MediaDevicePreferences;
  onMicToggle: (next: boolean) => void;
}) {
  const { t, dir } = useI18n();
  const [officers, setOfficers] = useState<Officer[]>([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // Only online + available officers are shown to the field side (server-filtered).
    api.get<Officer[]>(officersEndpoint(true))
      .then((list) => { if (active) setOfficers(list); })
      .catch((e: unknown) => { if (active) setError(String(e)); });
    return () => { active = false; };
  }, [reloadKey]);

  const filtered = useMemo(
    () => officers.filter((o) => q === "" || o.displayName.includes(q) || (o.unit ?? "").includes(q)),
    [officers, q]
  );

  return (
    <main
      dir={dir}
      style={{
        padding: 24, fontFamily: "Segoe UI, Arial, sans-serif", maxWidth: 980, margin: "0 auto",
        display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start"
      }}
    >
      {/* Local preview + mic control for the chosen setup (sticky on desktop, stacks on mobile). */}
      <div style={{ flex: "1 1 260px", minWidth: 240, maxWidth: 360, position: "sticky", top: 12 }}>
        <LocalWaitingPreview prefs={prefs} onMicToggle={onMicToggle} />
      </div>

      <div style={{ flex: "2 1 340px", minWidth: 280 }}>
        <h1 style={{ color: "#14305f", fontSize: 19, marginTop: 0 }}>{t.call.precallTitle}</h1>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.call.search}
          style={{ width: "100%", padding: "8px 12px", border: "1px solid #d6deea", borderRadius: 6, marginBottom: 14, boxSizing: "border-box" }}
        />
        {error && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {actionError && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>{actionError}</div>}
        {filtered.length === 0 && <p style={{ color: "#5b6b82", fontSize: 14 }}>{t.call.noOfficers}</p>}
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {filtered.map((o) => (
          <li
            key={o.officerId}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 12px", border: "1px solid #d6deea", borderRadius: 8, marginBottom: 8, background: "#fff"
            }}
          >
            <span>
              <strong style={{ color: "#1e2a3a" }}>{o.displayName}</strong>
              {o.unit && <span style={{ color: "#64748b", fontSize: 13 }}> · {o.unit}</span>}
              <span style={{ marginInlineStart: 10, fontSize: 12, color: o.available ? "#15803d" : "#64748b" }}>
                ● {o.available ? t.call.available : t.call.unavailable}
              </span>
            </span>
            {o.available && (
              <button
                disabled={!canCall}
                onClick={() => onCall(o.userId, o.displayName)}
                style={{
                  background: canCall ? "#1d4ed8" : "#94a3b8",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 16px",
                  cursor: canCall ? "pointer" : "not-allowed"
                }}
              >
                {t.call.callAction}
              </button>
            )}
          </li>
          ))}
        </ul>
      </div>
    </main>
  );
}
